// The session's routine kit from the unit store (spec Q7): the inventory
// module's own patient-consumption form, embedded exactly as OT charge capture
// embeds it, and submitted with the same three exported calls in the same
// order as OT's submitConsumptionBranch (create, confirm, capture charges).
// Items the per-session package includes are absorbed by its inclusion rules.

import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";

import PatientConsumptionSection from "../../ot-charge-capture/PatientConsumptionSection";
import { confirmConsumption, createConsumption } from "../../inventory/api/dispensing";
import { captureChargesAfterPatientConfirm } from "../../inventory/utils/consumptionChargeCapture";
import { ensureBillingSetup } from "../../ipd-billing/package-billing-api";
import { IPD_COLORS, IpdButton } from "../../ipd/utils";

// The inventory service validates ids as strings, as the inventory module's
// own patient picker sends them (SelectPatientDialog's toOptionalId).
export function consumptionPatient(session, billingAccountId) {
  return {
    patient_id: session.patient_id,
    admission_id: String(session.admission_id),
    billing_account_id: billingAccountId,
    name: session.patient_name,
    uhid: session.patient_id,
  };
}

export default function ConsumptionPanel({ session }) {
  const formApiRef = useRef(null);
  const [billingAccountId, setBillingAccountId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Idempotent: returns the admission's billing account, creating it if needed.
    ensureBillingSetup(session.admission_id)
      .then((setup) => setBillingAccountId(setup.billingAccountId || null))
      .catch((err) => toast.error(`Billing is not set up for this admission: ${err.message}`));
  }, [session.admission_id]);

  const patient = consumptionPatient(session, billingAccountId);

  const record = async () => {
    const collected = formApiRef.current?.collect();
    if (!collected || collected.skipped) {
      toast.error("Add at least one item");
      return;
    }
    if (collected.error) {
      toast.error(collected.error);
      return;
    }
    const { body, lines } = collected;
    setBusy(true);
    try {
      const created = await createConsumption(body);
      const id = created?.transaction_id || created?.id;
      if (!id) throw new Error("Consumption was created but no id was returned");
      await confirmConsumption(id, { remarks: body.remarks || "" });
      await captureChargesAfterPatientConfirm({
        ...created,
        id,
        transaction_id: id,
        consumption_type: "PATIENT",
        admission_id: body.admission_id,
        items: lines || body.items,
      });
      toast.success("Kit recorded against this session");
      formApiRef.current.reset();
    } catch (err) {
      toast.error(err.message || "Could not record the consumption");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      <PatientConsumptionSection patient={billingAccountId ? patient : null} formApiRef={formApiRef} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
          Pick the unit store and the items given this session (dialyser, lines, needles, heparin, saline…).
        </Typography>
        <IpdButton size="small" loading={busy} disabled={busy || !billingAccountId} onClick={record}>
          Record kit used
        </IpdButton>
      </Box>
    </Box>
  );
}
